/**
 * Where a signed-out fan is sent to sign in.
 *
 * `/auth/login?return_to=<here>` — one of the three shell routes `App\Auth\Routes` serves. It has
 * to be a top-level GET navigation, because the page it renders is what runs the SDK's
 * `loginWithRedirect()`, so this produces an href for an `<a>` and never something to fetch.
 *
 * **An href rather than `urcAuth.login()`, and that is a choice about the control and not about
 * the mechanism.** `LoyaltyJoinHero` renders an `<a>` the moment it is given one, which is what a
 * navigation should be: middle-clickable, copyable, and announced as a link. `login()` is the right
 * call from a handler that has already decided to navigate — `JoinBlock`'s `startJoin()` uses it —
 * but a CTA that is only ever "go and sign in" is a link.
 *
 * The base comes from `window.urcLoyalty` (`App\Loyalty\LoyaltyAssets`), not from
 * `window.location.origin`: under a subdirectory install the origin is not what WordPress serves,
 * and `Auth0Config::url()` builds the value from `home_url()`, which is the only thing that knows
 * the difference. `resources/scripts/auth/returnTo.js` then validates `return_to` in the browser,
 * comparing whole origins.
 */

/**
 * This page, as somewhere to come back to.
 *
 * **The two `auth0_*` parameters this used to strip are gone with the handler that added them.**
 * `?auth0_login=1` and `?auth0_success=1` were `auth0-login.php`'s round trip, and `return_to` now
 * travels in the SDK's `appState` rather than in a query string — so nothing accumulates on the URL
 * and there is nothing to remove.
 *
 * @return {string} An absolute same-origin URL.
 */
function returnTo() {
	return window.location.href;
}

/**
 * The sign-in link for the current page.
 *
 * `return_to` is a deliberate improvement on the site header, which links to the bare login URL and
 * therefore lands the fan on the home page. Sending them back here means they arrive on the join
 * hero, signed in, one click from joining.
 *
 * @return {string|undefined} The absolute login URL, or undefined when the runtime config is
 *                            missing -- in which case the caller renders no CTA rather than one
 *                            that navigates nowhere.
 */
export function signInHref() {
	const base = ( window.urcLoyalty && window.urcLoyalty.loginUrl ) || '';

	if ( ! base ) {
		console.warn( '[loyalty] no login URL in urcLoyalty config' );

		return undefined;
	}

	const url = new URL( base, window.location.origin );

	url.searchParams.set( 'return_to', returnTo() );

	return url.toString();
}
